import { createPortal } from "react-dom";
import { Bookmark, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { ApiError, apiClient, type SavedFilter } from "@/shared/api/api-client";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import type { MentionFilterCriteria } from "@/features/mentions/mention-filter-criteria";

import { errorMessage } from "@/shared/api/http";

import { formatShortDate } from "@/shared/utils/format";
import { useDismiss } from "@/shared/hooks/useDismiss";
/**
 * Salvar o recorte atual.
 *
 * Segue o `.confirm-dialog`: é um campo e um botão, exatamente o caso
 * que aquele componente resolve. O preview usa os MESMOS rótulos dos
 * chips da tela, porque "o que estou salvando" tem que ser lido igual
 * ao que a pessoa está vendo.
 *
 * Nome repetido não sobrescreve calado: o servidor responde 409 e o
 * diálogo pergunta se é pra substituir. Como o conflito pode ser com um
 * filtro criado por outra pessoa depois da última listagem, a resposta
 * do "substituir" recarrega a lista pra achar o id — em vez de confiar
 * num id que talvez nem estivesse na tela.
 */
export function SaveFilterDialog({
  token,
  criteria,
  chips,
  onSaved,
  onClose
}: {
  token: string;
  criteria: MentionFilterCriteria;
  chips: string[];
  onSaved: (filter: SavedFilter) => void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ name: string; message: string } | null>(null);

  const save = async (name: string) => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await apiClient.createSavedFilter(token, { kind: "mentions", name, criteria }));
    } catch (failure) {
      if (failure instanceof ApiError && failure.status === 409) {
        // A mensagem do servidor nomeia o filtro que JÁ existe — que
        // pode diferir do que a pessoa digitou só na caixa. Repetir o
        // texto digitado aqui diria que existe algo com um nome que não
        // é o que está gravado.
        setConflict({ name, message: failure.message });
      } else {
        setError(errorMessage(failure, "Não foi possível salvar o filtro."));
      }
    } finally {
      setBusy(false);
    }
  };

  const replace = async (name: string) => {
    setBusy(true);
    setError(null);
    try {
      const existing = (await apiClient.listSavedFilters(token, "mentions")).find(
        filter => filter.name.trim().toLowerCase() === name.trim().toLowerCase()
      );
      // Sumiu entre o conflito e agora (alguém apagou): cria, em vez de
      // devolver um erro que não descreve mais a realidade.
      onSaved(
        existing
          ? await apiClient.updateSavedFilter(token, existing.id, { name, criteria })
          : await apiClient.createSavedFilter(token, { kind: "mentions", name, criteria })
      );
    } catch (failure) {
      setError(
        errorMessage(failure, "Não foi possível substituir o filtro.")
      );
      setConflict(null);
    } finally {
      setBusy(false);
    }
  };

  if (conflict) {
    return (
      <ConfirmDialog
        title="Substituir filtro?"
        description={`${conflict.message} Substituir troca o recorte dele pelo atual, para todo mundo da empresa.`}
        confirmLabel="Substituir"
        loading={busy}
        error={error}
        onConfirm={() => void replace(conflict.name)}
        onCancel={() => setConflict(null)}
      />
    );
  }

  return (
    <ConfirmDialog
      title="Salvar filtro"
      description={
        chips.length > 0
          ? `Este recorte fica disponível para todo mundo da empresa: ${chips.join(" · ")}`
          : "Este recorte fica disponível para todo mundo da empresa."
      }
      confirmLabel="Salvar"
      loading={busy}
      error={error}
      requireField={{
        label: "Nome do filtro",
        type: "text",
        placeholder: "Ex.: Crise dos Correios",
        maxLength: 80
      }}
      onConfirm={name => void save(name)}
      onCancel={onClose}
    />
  );
}

/**
 * Carregar (ou apagar) um filtro salvo.
 *
 * Usa `.modal-overlay`/`.modal-container` e não o `.confirm-dialog`: a
 * lista tem tamanho variável, e o confirm não tem área de rolagem — com
 * vinte filtros salvos os últimos ficariam fora da viewport, sem
 * alcance.
 *
 * A tecla Esc é suprimida enquanto a confirmação de exclusão está
 * aberta: os dois diálogos escutam `keydown` no documento e nenhum para
 * a propagação, então um Esc fecharia a pilha inteira de uma vez.
 */
export function LoadFilterDialog({
  token,
  onApply,
  onClose
}: {
  token: string;
  onApply: (filter: SavedFilter) => void;
  onClose: () => void;
}) {
  const [filters, setFilters] = useState<SavedFilter[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<SavedFilter | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setFilters(await apiClient.listSavedFilters(token, "mentions"));
      } catch (failure) {
        setError(
          errorMessage(failure, "Não foi possível carregar os filtros.")
        );
        setFilters([]);
      }
    })();
  }, [token]);

  // Com a confirmação aberta, o Esc é dela.
  useDismiss(!removing, () => onClose());

  const remove = async (filter: SavedFilter) => {
    setBusy(true);
    try {
      await apiClient.deleteSavedFilter(token, filter.id);
      setFilters(current => (current ?? []).filter(item => item.id !== filter.id));
      setRemoving(null);
    } catch (failure) {
      setError(
        errorMessage(failure, "Não foi possível excluir o filtro.")
      );
      setRemoving(null);
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div
          className="modal-container saved-filters-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="saved-filters-title"
          onClick={event => event.stopPropagation()}
        >
          <header className="modal-header">
            <div>
              <span className="eyebrow">FILTROS SALVOS</span>
              <h2 id="saved-filters-title">Carregar filtro</h2>
            </div>
            <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar">
              <X size={18}/>
            </button>
          </header>

          <div className="modal-body">
            {error && <p className="saved-filters-empty">{error}</p>}
            {filters === null && <p className="saved-filters-empty">Carregando…</p>}
            {filters !== null && filters.length === 0 && !error && (
              <p className="saved-filters-empty">
                Nenhum filtro salvo ainda. Monte um recorte na tela e use “Salvar filtro” — ele
                fica disponível para todo mundo da empresa.
              </p>
            )}
            {filters !== null && filters.length > 0 && (
              <ul className="saved-filters-list">
                {filters.map(filter => (
                  <li key={filter.id}>
                    <div className="saved-filters-info">
                      <strong title={filter.name}>{filter.name}</strong>
                      <span>
                        {filter.updated_at
                          ? `editado em ${formatShortDate(new Date(filter.updated_at))}`
                          : `criado em ${formatShortDate(new Date(filter.created_at))}`}
                      </span>
                    </div>
                    <div className="saved-filters-actions">
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => onApply(filter)}
                      >
                        Aplicar
                      </button>
                      <button
                        type="button"
                        className="icon-button"
                        onClick={() => setRemoving(filter)}
                        aria-label={`Excluir ${filter.name}`}
                      >
                        <Trash2 size={16}/>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {removing && (
        <ConfirmDialog
          title="Excluir filtro salvo"
          description={`"${removing.name}" deixa de aparecer para todo mundo da empresa. As menções não são afetadas.`}
          confirmLabel="Excluir"
          danger
          loading={busy}
          onConfirm={() => void remove(removing)}
          onCancel={() => setRemoving(null)}
        />
      )}
    </>,
    document.body
  );
}

/** O botão que abre o carregador — vive ao lado de "Mais filtros". */
export function SavedFiltersButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="secondary-button" onClick={onClick}>
      <Bookmark size={15}/> Filtros salvos
    </button>
  );
}
